<script lang="ts" setup>
import { useManagementStore } from "@/features/management/management.store";
import { computed, onMounted, ref, watch } from "vue";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Pencil02,
  SearchMd,
  Trash01,
  Wallet04,
  XClose,
  Check,
} from "utruckin-icons";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { useShiftStore } from "@/features/shifts/shifts.store";
import CommonModal from "@/components/common/CommonModal.vue";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import { DateTime } from "luxon";
import {
  IUpdateAdvancePayload,
  IUpdateBonusPayload,
  IUpdateSalaryPayload,
} from "@/features/management/management.types";
import { useNotification } from "@/utils/notification";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { toRaw } from "vue";

const managementStore = useManagementStore();
const shiftStore = useShiftStore();

const { pushNotification } = useNotification();

const search = ref<string>("");
let searchTimer: ReturnType<typeof setTimeout> | null = null;

const loading = ref<boolean>(false);
const list = ref<any[]>([]);
const summary = ref<any>();

const shifts = ref<IShiftTemplate[]>([]);
const shiftLoading = ref(false);

const isModalVisible = ref<boolean>(false);
const modalItem = ref<any>();
const oldModalItem = ref<any>();
const modalLoading = ref<boolean>(false);

const numberAdvanceRequests = ref<number>(0);
const isAdvanceModalVisible = ref<boolean>(false);
const advanceModalItems = ref<any>();
const advanceModalLoading = ref<boolean>(false);

const activeTab = ref<number>(0);
const tabs = [
  { label: "Salary", value: 0 },
  { label: "Absent", value: 1 },
  { label: "Rating", value: 2 },
];

const timeliness_score = ref<number>(1);
const coverage_score = ref<number>(1);
const team_score = ref<number>(1);
const no_shows_score = ref<number>(1);
const ratingComment = ref<string>("");

const absentDays = ref<any[]>([]);
const oldAbsentDays = ref<any[]>([]);

const currentShiftIndex = ref<number>(0);
const shiftItems = computed(() => {
  let result = shifts.value.map((s) => ({
    value: String(s.id),
    name: s.title,
  }));
  result.unshift({ value: "", name: "All shift" });

  return result;
});

const currentShiftItem = computed(
  () => shiftItems.value[currentShiftIndex.value % shiftItems.value.length]
);

const getList = async () => {
  await managementStore.getHrList(loading, (res: any) => {
    if (res) {
      list.value = res.items;
      summary.value = res.summary;
    }
  });
};

const getShifts = async () => {
  [shifts.value] = await Promise.all([shiftStore.getShifts(shiftLoading)]);
};

const previousMonth = () => {
  managementStore.now = managementStore.now?.minus({ months: 1 });
};

const nextMonth = () => {
  managementStore.now = managementStore.now?.plus({ months: 1 });
};

const selectItem = async (item: any) => {
  await managementStore.getUserDetail(
    modalLoading,
    item.user_id,
    {
      year: managementStore.managementFilter.year,
      month: managementStore.managementFilter.month,
    },
    (data: any) => {
      isModalVisible.value = true;
      modalItem.value = item;
      oldModalItem.value = { ...item };

      if (data.ratings.length) {
        timeliness_score.value = data.ratings[0].timeliness_score;
        coverage_score.value = data.ratings[0].coverage_score;
        team_score.value = data.ratings[0].team_score;
        no_shows_score.value = data.ratings[0].no_shows_score;
        ratingComment.value = data.ratings[0].comment;
      }

      if (data.extra_offs.length) {
        absentDays.value = data.extra_offs;
        oldAbsentDays.value = structuredClone(toRaw(absentDays.value));
      }
    }
  );
};

const closeModal = () => {
  isModalVisible.value = false;
  modalItem.value = undefined;
  oldAbsentDays.value = [];
  absentDays.value = [];

  timeliness_score.value = 1;
  coverage_score.value = 1;
  team_score.value = 1;
  no_shows_score.value = 1;
  ratingComment.value = "";
};

const saveSalary = async () => {
  if (!modalItem.value || modalLoading.value) return;

  const isSalaryChanged =
    modalItem.value.salary_payable !== oldModalItem.value.salary_payable;
  const isBonusChanged =
    modalItem.value.bonus_total !== oldModalItem.value.bonus_total;
  const isAdvanceChanged =
    modalItem.value.advance_paid_total !==
    oldModalItem.value.advance_paid_total;

  const todayDate = DateTime.now().toFormat("yyyy-MM-dd");

  const payloadSalary: IUpdateSalaryPayload = {
    new_monthly_wage: modalItem.value.salary_payable,
    effective_date: todayDate,
    reason: "no reason",
  };

  const payloadBonus: IUpdateBonusPayload = {
    amount: modalItem.value.bonus_total,
    currency: "USD",
    starts_on: todayDate,
    ends_on: todayDate,
    reason: "no reason",
  };

  const payloadAdvance: IUpdateAdvancePayload = {
    advance_month: todayDate,
    amount: modalItem.value.advance_paid_total,
    currency: "USD",
    reason: "no reason",
  };

  await Promise.all([
    isSalaryChanged
      ? managementStore.updateSalary(
          modalLoading,
          modalItem.value.user_id,
          payloadSalary,
          (res: any) => {
            if (res) {
              pushNotification("Successfully updated salary", "success");
            }
          }
        )
      : "",

    isBonusChanged
      ? managementStore.updateBonus(
          modalLoading,
          modalItem.value.user_id,
          payloadBonus,
          (res: any) => {
            if (res) {
              pushNotification("Successfully updated bonus", "success");
            }
          }
        )
      : "",

    isAdvanceChanged
      ? managementStore.updateAdvance(
          modalLoading,
          modalItem.value.user_id,
          payloadAdvance,
          (res: any) => {
            if (res) {
              pushNotification("Successfully updated advance", "success");
            }
          }
        )
      : "",
  ]);

  closeModal();
};

const prev = () => {
  currentShiftIndex.value =
    (currentShiftIndex.value - 1 + shiftItems.value.length) %
    shiftItems.value.length;
};

const next = () => {
  currentShiftIndex.value++;
};

const setTimelinessScore = (value: number) => {
  timeliness_score.value = value;
};

const setCoverageScore = (value: number) => {
  coverage_score.value = value;
};

const setTeamScore = (value: number) => {
  team_score.value = value;
};

const setNoShowsScore = (value: number) => {
  no_shows_score.value = value;
};

const saveRating = async () => {
  if (!modalItem.value || modalLoading.value) return;

  const todayDate = DateTime.now().toFormat("yyyy-MM-dd");

  await managementStore.updateRating(
    modalLoading,
    modalItem.value.user_id,
    {
      comment: ratingComment.value,
      rating_month: todayDate,
      coverage_score: coverage_score.value,
      no_shows_score: no_shows_score.value,
      team_score: team_score.value,
      timeliness_score: timeliness_score.value,
    },
    async (res: any) => {
      if (res) {
        pushNotification("Successfully updated rating", "success");
        closeModal();
        await getList();
        await getAdvanceRequests();
      }
    }
  );
};

const saveAbsent = async () => {
  if (!modalItem.value || modalLoading.value) return;

  for (const item of absentDays.value) {
    if (item.id) {
      const obj2 = oldAbsentDays.value.find((obj) => obj.id == item.id);

      if (obj2 && JSON.stringify(item) !== JSON.stringify(obj2)) {
        await managementStore.updateOff(
          modalLoading,
          modalItem.value.user_id,
          item.id,
          {
            affects_rating: item.affects_rating,
            comment: item.comment,
            is_paid: item.is_paid,
            off_date: item.off_date,
            reason: item.reason,
            reason_code: item.reason_code,
          },
          (res: any) => {
            if (res) {
              pushNotification("Successfully updated", "success");
            }
          }
        );
      }
    } else {
      if (item.off_date) {
        await managementStore.createOff(
          modalLoading,
          modalItem.value.user_id,
          {
            affects_rating: true,
            comment: item.comment,
            is_paid: false,
            off_date: item.off_date,
            reason: "string",
            reason_code: "string",
          },
          (res: any) => {
            if (res) {
              pushNotification("Successfully created", "success");
            }
          }
        );
      }
    }
  }
  await getList();
  await getAdvanceRequests();
};

const addExtraOff = () => {
  absentDays.value.push({
    off_date: "",
    comment: "",
  });
};

const deleteAbsentDay = async (index: number, item: any) => {
  if (item.id) {
    console.log(item);
    await managementStore.deleteOff(
      modalLoading,
      item.user_id,
      item.id,
      (data: any) => {
        if (!data) {
          return;
        } else {
          closeModal();
          pushNotification("Successfully deleted", "success");
        }
      }
    );
  } else {
    absentDays.value.splice(index, 1);
  }
};

const getAdvanceRequests = async () => {
  await managementStore.getAdvanceRequestsList(loading, (res: any) => {
    if (res) {
      numberAdvanceRequests.value = res.total;
      advanceModalItems.value = res.items;
    }
  });
};

const openAdvanceModal = () => {
  isAdvanceModalVisible.value = true;
  advanceModalLoading.value = false;
};

const closeAdvanceModal = () => {
  isAdvanceModalVisible.value = false;
  advanceModalLoading.value = false;
};

const rejectRequest = async (item: any) => {
  await managementStore.rejectAdvance(
    advanceModalLoading,
    item.user_id,
    item.id,
    async (res: any) => {
      if (res) {
        pushNotification("Successfully rejected", "success");
        closeAdvanceModal();
        await getList();
        await getAdvanceRequests();
      }
    }
  );
};

const approveRequest = async (item: any) => {
  await managementStore.approveAdvance(
    advanceModalLoading,
    item.user_id,
    item.id,
    async (res: any) => {
      if (res) {
        pushNotification("Successfully aproved", "success");
        closeAdvanceModal();
        await getList();
        await getAdvanceRequests();
      }
    }
  );
};

function datepickerToUtcDateOnly(d: any) {
  return d
    ? DateTime.fromISO(d, {
        zone: "utc",
      })
        .setZone("America/New_York")
        .toFormat("MMM d, HH:mm")
    : "-";
}

onMounted(async () => {
  await getList();
  await getShifts();
  await getAdvanceRequests();
});

watch(
  () => managementStore.now,
  (date) => {
    managementStore.managementFilter.year = date.year;
    managementStore.managementFilter.month = date.month;
    getList();
  },
  { deep: true }
);

watch(
  () => currentShiftItem.value.value,
  (val, oldVal) => {
    if (val === oldVal) return;
    managementStore.managementFilter.shift_template_id = val ? +val : null;
    getList();
  }
);

watch(
  () => search.value,
  async (val) => {
    if (loading.value) return;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      managementStore.managementFilter.q = val;
      await getList();
    }, 400);
  }
);
</script>

<template>
  <div class="v-management">
    <div class="header">
      <div class="control">
        <div class="year">
          <div class="left" @click="previousMonth">
            <div class="icon">
              <ChevronLeft :size="14" />
            </div>
          </div>
          <div class="label">
            {{ managementStore.now?.toFormat("LLL yyyy") }}
          </div>
          <div class="right" @click="nextMonth">
            <div class="icon">
              <ChevronRight :size="14" />
            </div>
          </div>
        </div>

        <div class="shift">
          <div class="left" @click="prev">
            <div class="icon">
              <ChevronLeft :size="14" />
            </div>
          </div>

          <div class="label">
            {{ currentShiftItem.name }}
          </div>

          <div class="right" @click="next">
            <div class="icon">
              <ChevronRight :size="14" />
            </div>
          </div>
        </div>
      </div>

      <div class="filter">
        <CommonSearch
          v-model="search"
          :icon="SearchMd"
          :placeholder="'Search'"
          clearable
        />

        <div class="advance" @click="openAdvanceModal">
          <div class="icon">
            <Wallet04 :size="20" />
          </div>
          <div class="label">Advance</div>
          <div class="number">{{ numberAdvanceRequests }}</div>
        </div>
      </div>
    </div>

    <div class="table">
      <table>
        <colgroup>
          <col style="width: 1%" />
          <col style="width: 19%" />
          <col style="width: 7%" />
          <col style="width: 12%" />
          <col style="width: 10%" />
          <col style="width: 12%" />
          <col style="width: 8%" />
          <col style="width: 6%" />
          <col style="width: 6%" />
          <col style="width: 6%" />
          <col style="width: 10%" />
          <col style="width: 4%" />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>Name</th>
            <th>Position</th>
            <th>Rating</th>
            <th>Hire date</th>
            <th>Last salary increase</th>
            <th>Current salary</th>
            <th>Advance</th>
            <th>Bonus</th>
            <th>Salary</th>
            <th>Absent days</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr class="belt">
            <td colspan="12">
              <div class="stats">
                <div class="capsule">
                  <div class="label">Users</div>
                  <div class="value">{{ summary?.users_total }}</div>
                </div>
                <div class="capsule">
                  <div class="label">Paid employees</div>
                  <div class="value">{{ summary?.paid_employees }}</div>
                </div>
                <div class="capsule">
                  <div class="label">Total advance</div>
                  <div class="value">{{ summary?.total_advance }}</div>
                </div>
                <div class="capsule">
                  <div class="label">Total bonus</div>
                  <div class="value">{{ summary?.total_bonus }}</div>
                </div>
                <div class="capsule">
                  <div class="label">Total salary</div>
                  <div class="value">{{ summary?.total_salary }}</div>
                </div>
              </div>
            </td>
          </tr>
          <tr v-for="(item, index) in list" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.role }}</td>
            <td>
              <div class="rating__cell">
                <div class="number">
                  {{ item.rating ? item.rating : 0 }}
                </div>
                <div
                  class="stars"
                  :style="{ '--rating': item.rating ? item.rating : 0 }"
                >
                  <div class="stars-bg">★★★★★</div>
                  <div class="stars-fg">★★★★★</div>
                </div>
              </div>
            </td>
            <td>{{ item.hire_duration_label || "-" }}</td>
            <td>{{ item.last_salary_increase_label || "-" }}</td>
            <td>${{ item.current_salary || "0" }}</td>
            <td>${{ item.advance_paid_total || "0" }}</td>
            <td>${{ item.bonus_total || "0" }}</td>
            <td>${{ item.salary_payable || "0" }}</td>
            <td>{{ item.absent_days_label || "-" }}</td>
            <td>
              <div class="action">
                <div class="icon" @click="selectItem(item)">
                  <Pencil02 :size="16" />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CommonModal
      v-model="isModalVisible"
      title="Edit user info"
      @close="closeModal"
    >
      <div class="tabs">
        <CustomTab :tabs="tabs" v-model="activeTab" />
      </div>

      <div class="name">
        {{ modalItem.name }}
      </div>

      <div class="salary" v-if="activeTab == 0">
        <CommonInput label="Salary" v-model="modalItem.salary_payable" />
        <CommonInput label="Bonus" v-model="modalItem.bonus_total" />
        <CommonInput label="Advance" v-model="modalItem.advance_paid_total" />

        <div class="salary__footer">
          <CommonButton
            :loading="modalLoading"
            type="outline"
            label="Cancel"
            @clicked="closeModal"
          />
          <CommonButton
            :loading="modalLoading"
            type="primary"
            label="Save"
            @click="saveSalary"
          />
        </div>
      </div>

      <div class="absent" v-if="activeTab == 1">
        <template v-if="absentDays.length">
          <template v-for="(item, index) in absentDays" :key="index">
            <div class="absent__date action">
              <div class="input">
                <CommonInput
                  v-model="item.off_date"
                  label="Absent day"
                  mask="####-##-##"
                  :disabled="item.id"
                />
              </div>

              <div class="icon" @click="deleteAbsentDay(index, item)">
                <Trash01 :size="16" />
              </div>
            </div>
            <div class="absent__comment">
              <CommonInput v-model="item.comment" multiline label="Comment" />
            </div>
          </template>
        </template>

        <div class="absent__add">
          <CommonButton
            type="outline"
            :loading="modalLoading"
            label="Add off"
            prepend
            :prependIcon="Plus"
            @clicked="addExtraOff"
          />
        </div>

        <div class="absent__footer">
          <CommonButton
            :loading="modalLoading"
            type="secondary"
            label="Cancel"
            @clicked="closeModal"
          />
          <CommonButton
            :loading="modalLoading"
            type="primary"
            label="Save"
            @click="saveAbsent"
          />
        </div>
      </div>

      <div class="rating" v-if="activeTab == 2">
        <div class="action">
          <div class="action__label">Timeliness</div>

          <div class="action__rating">
            <span
              class="star"
              v-for="star in 5"
              :key="star"
              @click="setTimelinessScore(star)"
            >
              <span :class="{ filled: star <= timeliness_score }">★</span>
            </span>
          </div>
        </div>
        <div class="action">
          <div class="action__label">Coverage</div>

          <div class="action__rating">
            <span
              class="star"
              v-for="star in 5"
              :key="star"
              @click="setCoverageScore(star)"
            >
              <span :class="{ filled: star <= coverage_score }">★</span>
            </span>
          </div>
        </div>
        <div class="action">
          <div class="action__label">Team</div>

          <div class="action__rating">
            <span
              class="star"
              v-for="star in 5"
              :key="star"
              @click="setTeamScore(star)"
            >
              <span :class="{ filled: star <= team_score }">★</span>
            </span>
          </div>
        </div>
        <div style="margin-bottom: 12px" class="action">
          <div class="action__label">No shows</div>

          <div class="action__rating">
            <span
              class="star"
              v-for="star in 5"
              :key="star"
              @click="setNoShowsScore(star)"
            >
              <span :class="{ filled: star <= no_shows_score }">★</span>
            </span>
          </div>
        </div>

        <CommonInput multiline label="Comment" v-model="ratingComment" />

        <div class="rating__footer">
          <CommonButton
            :loading="modalLoading"
            type="outline"
            label="Cancel"
            @clicked="closeModal"
          />
          <CommonButton
            :loading="modalLoading"
            type="primary"
            label="Save"
            @click="saveRating"
          />
        </div>
      </div>
    </CommonModal>

    <CommonModal
      v-model="isAdvanceModalVisible"
      width="1000px"
      title="Advance requests"
      @close="closeAdvanceModal"
    >
      <CommonTable
        :headers="managementStore.advanceRequestHeaders"
        :loading="advanceModalLoading"
        :items="advanceModalItems"
      >
        <template #id="{ index }">
          <span>{{ index + 1 }}</span>
        </template>

        <template #created_time="{ item }">
          <span>{{ datepickerToUtcDateOnly(item.created_time) || "-" }}</span>
        </template>

        <template #actions="{ item }">
          <div class="advance__action">
            <div
              class="action__item reject"
              v-tooltip="{
                content: 'Reject',
                html: true,
              }"
              @click="rejectRequest(item)"
            >
              <XClose :size="18" />
            </div>
            <div
              class="action__item approve"
              v-tooltip="{
                content: 'Approve',
                html: true,
              }"
              @click="approveRequest(item)"
            >
              <Check :size="18" />
            </div>
          </div>
        </template>
      </CommonTable>
    </CommonModal>
  </div>
</template>

<style lang="scss" scoped>
.v-management {
  height: calc(100vh - 80px);
  background: var(--color-system-background);
  color: var(--color-label);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 16px 0 16px;

    .control {
      display: flex;
      align-items: center;
      gap: 10px;

      .year,
      .shift {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border: 1px solid var(--color-border);
        border-radius: 14px;

        .label {
          font-size: 16px;
          line-height: 24px;
        }

        .icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
      }
    }

    .filter {
      display: flex;
      align-items: center;
      gap: 8px;

      .advance {
        border: 1px solid var(--color-border);
        border-radius: 14px;
        background-color: var(--color-green-light);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 11px 14px;
        gap: 8px;
        cursor: pointer;

        .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 20px;
          height: 20xp;
        }

        .label {
          color: var(--color-label);
        }

        .number {
          background: var(--color-system-green);
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-white);
        }
      }
    }
  }

  .table {
    background: var(--color-system-background);
    padding: var(--base-space-16);
    border-radius: 20px;
    overflow: auto;

    table {
      width: 100%;
      border-collapse: separate;
      border: 1px solid var(--color-border);
      border-radius: 14px;
      border-spacing: 0;
      overflow: hidden;

      th,
      td {
        border-right: 1px solid var(--color-border);
        border-bottom: 1px solid var(--color-border);
        padding: 10px 16px;
        font-size: 14px;
        line-height: 20px;
      }

      th {
        color: var(--color-label-secondary);
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      tr:last-child td {
        border-bottom: none;
      }

      .rating__cell {
        display: flex;
        align-items: center;
        gap: 8px;
        .stars {
          position: relative;
          display: inline-block;
          font-size: 24px;
          line-height: 1;
        }

        .stars-bg {
          color: #ddd; /* empty star color */
        }

        .stars-fg {
          position: absolute;
          top: 0;
          left: 0;
          color: #f5b301; /* filled star color */
          overflow: hidden;
          width: calc(var(--rating) / 5 * 100%);
          white-space: nowrap;
        }
      }

      .belt {
        padding: 0;

        td {
          padding: 0;

          .stats {
            display: flex;
            gap: 8px;
            background: var(--color-secondary-system-background);
            padding: 12px;

            .capsule {
              display: flex;
              align-items: center;
              border: 1px solid var(--color-border);
              border-radius: 12px;
              overflow: hidden;

              .label {
                padding: 7px 8px 7px 12px;
                background: var(--color-system-background);
                font-size: 14px;
                line-height: 18px;
                font-weight: 400;
              }

              .value {
                padding: 7px 12px 7px 8px;
                background: var(--color-secondary-system-background);
                font-size: 14px;
                line-height: 20px;
                font-weight: 600;
              }
            }
          }
        }
      }

      .action {
        .icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: 1px solid var(--color-border);
          border-radius: 12px;
          background: var(--color-secondary-system-background);
        }
      }
    }
  }
}

.tabs {
  display: flex;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 16px;
}

.name {
  margin: 16px 0;
  font-size: 16px;
  line-height: 28px;
  font-weight: 600;
  color: var(--color-label);
}

.salary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--color-label);

  &__salary,
  &__bonus,
  &__advance {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    padding: 16px 0 0 0;
    margin-top: 24px;
    border-top: 1px solid var(--color-border);
  }

  .checkbox {
    width: 52px;
    height: 52px;
    border: 1px solid var(--color-border);
    background: var(--color-secondary-system-background);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    input {
      display: none;
    }

    .checkmark {
      width: 24px;
      height: 24px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-white);

      &.active {
        border-color: var(--color-primary);
        background: var(--color-primary);
      }
    }
  }
}

.rating {
  color: var(--color-label);
  .action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px;
    border-radius: 14px;
    background: var(--color-secondary-system-background);
    border: 1px solid var(--color-border);
    margin-top: 8px;

    &:last-child {
      margin-top: 12px;
    }

    &__label {
      font-size: 16px;
      line-height: 24px;
      font-weight: 500;
    }

    &__rating {
      display: flex;
      align-items: center;
      gap: 4px;

      .star {
        border: none;
        background: none;
        padding: 0;
        cursor: pointer;

        span {
          font-size: 24px;
          color: #d9d9d9;
          transition: color 0.15s ease;

          &.filled {
            color: #ffc107;
          }
        }
      }
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    padding: 16px 0 0 0;
    margin-top: 24px;
    border-top: 1px solid var(--color-border);
  }
}

.absent {
  color: var(--color-label);
  &__date {
    margin-bottom: 8px;

    .input {
      flex-grow: 1;
    }

    &.action {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .icon {
      width: 52px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--color-border);
      border-radius: 14px;
      background: var(--color-secondary-system-background);
      color: var(--color-system-red);
      cursor: pointer;
    }
  }

  &__comment {
    margin-bottom: 8px;
  }

  &__add {
    display: flex;
    justify-content: end;
    align-items: center;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    padding: 16px 0 0 0;
    margin-top: 24px;
    border-top: 1px solid var(--color-border);
  }
}

.advance__action {
  display: flex;
  align-items: center;
  gap: 8px;

  .action__item {
    width: 32px;
    height: 32px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-secondary-system-background);

    &.approve {
      background: var(--color-system-green);
      color: var(--color-white);
    }
  }
}
</style>
